import { useCallback } from 'react'

import { ReportDetailsRoot, ReportSection } from 'app/common/templates-next/report-template'
import { Header } from './Header'
import { TableLoading } from './TableLoading'
import { DataTable } from './dataTable'
import { useSelection } from './useSelection'
import { SelectionActions } from './SelectionActions'

import { Summary } from '../summary'
import { useCurrencySign } from 'app/common/components-next/currency'
import { useReportResult } from 'app/dataProviders/reports/hooks'
import { FinanceReportType } from 'app/dataTypes/reports/finances'
import { ReportEncashmentsData, TotalEncashmentsSummaryType } from 'app/dataTypes/reports/data'
import { ReportStatusIndicator } from 'app/components/common/reports/v3/ReportStatusIndicator'
import { useEncashmentsAction, useEncashmentsActionsAll, useEncashmentsStatusAll } from 'app/dataProviders/encashments/hooks'
import SimpleDate from 'app/common/classes/simple-date'
import { useDataResult } from './useDataResult'


const emptySummary: TotalEncashmentsSummaryType = {
  balanceAtTheBeginning: '0.00',
  revenue: '0.00',
  collected: '0.00',
  balanceAtTheEnd: '0.00',
  statuses: {
    cashAccepted: '0.00',
    cashRefused: '0.00',
    waitingForCash: '0.00',
    cashNotDeposited: '0.00',
  }
}

const emptyData: ReportEncashmentsData = {
  date: SimpleDate.createToday(),
  rows: [],
  totalSummary: emptySummary
}

type Props = {
  reportID: null | string
  resetReport: () => void
}

export const Result = ({ reportID, resetReport }: Props) => {

  const { requestResult, handleChangeStatus, data, loading } = useDataResult(reportID)
  const { totalSummary: summaryData = null, date, rows } = data || emptyData

  const {
    selection,
    selectionMode,
    setSelectionMode,
    changeSelection,
    reset,
    selectedAll,
    changeSelectionAll,
  } = useSelection(rows)

  const { hasItemsToRecord, hasItemsToCancel } = useEncashmentsStatusAll(rows)


  const actionsAll = useEncashmentsActionsAll({
    actionCb: handleChangeStatus,
    selection,
    date,
    action: selectionMode
  })

  const handleClickAction = useCallback(async (comment?: string) => {
    reset()
    const res = await actionsAll(comment)
    return res
  }, [reset, actionsAll])

  const currency = useCurrencySign()



  const header = !reportID ? '' : 'Отчет «Инкассация»'

  return (
    reportID && <ReportSection
      skipPadding
      footerBar={
        !!selectionMode
        && <SelectionActions
          selectionNumber={selection.size}
          onClickCancel={reset}
          onClickAction={handleClickAction}
          selectionMode={selectionMode}
        />
      }
    >
      <ReportStatusIndicator
        reportID={reportID}
        onFail={resetReport}
        onSuccess={requestResult}
      />

      <Summary
        totalSummaryData={summaryData || emptySummary}
        currency={currency}
        loading={loading}
      />

      <ReportDetailsRoot header={header}>
        <Header
          showRecordButton={hasItemsToRecord}
          showCancelButton={hasItemsToCancel}
          onSelectionMode={setSelectionMode}
          disabled={loading}
        />

        {loading
          && <TableLoading />}

        {!loading
          && <DataTable
            data={rows ?? []}
            date={date}
            currency={currency}
            onChangeStatus={handleChangeStatus}
            selectionMode={selectionMode}
            selection={selection}
            onChangeSelection={changeSelection}
            onChangeSelectionAll={changeSelectionAll}
            selectedAll={selectedAll}
            showSelectAllCheckbox={!!selectionMode}
            hasStickyHeader={hasItemsToRecord || hasItemsToCancel}
          />}
      </ReportDetailsRoot>
    </ReportSection>
  )
}
